import { useEffect, useRef } from 'react';

import type { SpriteAnimationProps } from './types';

// Canvas-based spritesheet player, copied from game-ui's SpriteAnimation.
// Draws one spritesheet frame per `frameInterval` ms via requestAnimationFrame.
export const SpriteAnimation = ({
  sprite,
  isPlaying,
  frameInterval = 83.33, // Default: ~12 FPS (1000/12)
  scale = 1,
  initialSprite,
  className,
  style,
  dataTestId,
  onCycleComplete,
  infiniteLoop = false, // Default: play once
}: SpriteAnimationProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameIndexRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef(0);
  const animationCompleted = useRef(false);

  // Draw initial (last) frame on mount or when the initial sprite changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !initialSprite.image) return;

    const frames = initialSprite.frames;
    const targetFrame = frames[frames.length - 1];
    canvas.width = Math.round(targetFrame.w * scale);
    canvas.height = Math.round(targetFrame.h * scale);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      initialSprite.image,
      targetFrame.x,
      targetFrame.y,
      targetFrame.w,
      targetFrame.h,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [initialSprite, scale]);

  // Animation loop — only starts when isPlaying becomes true.
  useEffect(() => {
    if (!isPlaying) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    if (!canvas || !ctx || !sprite.ready || !sprite.image || sprite.frames.length === 0) return;

    // Reset animation state when starting to play.
    frameIndexRef.current = 0;
    animationCompleted.current = false;
    lastFrameTimeRef.current = 0;

    const drawFrameAt = (index: number) => {
      const frame = sprite.frames[index];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(sprite.image!, frame.x, frame.y, frame.w, frame.h, 0, 0, canvas.width, canvas.height);
    };

    const animate = (currentTime: number) => {
      if (currentTime - lastFrameTimeRef.current >= frameInterval) {
        const currentIndex = frameIndexRef.current;
        drawFrameAt(currentIndex);

        const finishedNextIndex = currentIndex + 1 === sprite.frames.length;
        if (finishedNextIndex) {
          animationCompleted.current = true;
          frameIndexRef.current = sprite.frames.length - 1; // Set to last frame
          drawFrameAt(frameIndexRef.current); // Draw last frame
          onCycleComplete?.();

          // If loop mode is enabled, restart animation.
          if (infiniteLoop) {
            frameIndexRef.current = 0;
            animationCompleted.current = false;
            lastFrameTimeRef.current = currentTime;
          } else {
            return; // Stop animation loop
          }
        } else {
          frameIndexRef.current = currentIndex + 1;
        }

        lastFrameTimeRef.current = currentTime;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isPlaying, sprite, frameInterval, scale, onCycleComplete, infiniteLoop]);

  return (
    <div className={className} data-testid={dataTestId} style={style}>
      <canvas ref={canvasRef} />
    </div>
  );
};
